import React, { useState } from "react";
import { api } from "../../lib/axios";
import { useNavigate } from "react-router-dom";

interface ApiResponse {
  qrcode: string;
  message: string;
}

const Enable2FA: React.FC = () => {
  const [qrcode, setQrcode] = useState<string>("");
  const [verificationCode, setVerificationCode] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const navigate = useNavigate();

  const generateQRCode = async () => {
    setIsLoading(true);
    setError("");
    try {
      const response = await api.post<ApiResponse>(
        "/enable-2fa",
        {},
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      setQrcode(response.data.qrcode);
      setMessage("Scan the QR code with your authenticator app");
    } catch (error: any) {
      setError(error.response?.data?.message || "Error enabling 2FA");
    } finally {
      setIsLoading(false);
    }
  };

  const verify2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const response = await api.post<ApiResponse>(
        "/verify-2fa",
        { token: verificationCode },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      setMessage(response.data.message);

      if (response.data.message === "2FA enabled successfully") {
        setTimeout(() => navigate("/admin/dashboard"), 1500);
      }
    } catch (error: any) {
      setError(error.response?.data?.message || "Verification failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Enable Two-Factor Authentication
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Enhance your account security with 2FA
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {!qrcode ? (
            <div className="space-y-6">
              <div className="text-sm text-gray-600 text-center">
                <p className="mb-4">
                  Two-factor authentication adds an extra layer of security to
                  your account by requiring a code from your phone in addition
                  to your password.
                </p>
                <button
                  onClick={generateQRCode}
                  disabled={isLoading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Generate QR Code
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="text-center">
                <div className="mb-6 p-4 bg-gray-50 rounded-lg inline-block">
                  <img
                    src={qrcode}
                    alt="QR Code"
                    className="mx-auto max-w-full h-auto"
                  />
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Scan this QR code with your authenticator app (Google
                  Authenticator, Authy, etc.)
                </p>
              </div>

              <form onSubmit={verify2FA} className="space-y-6">
                <div>
                  <label
                    htmlFor="verificationCode"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Verification Code
                  </label>
                  <div className="mt-1">
                    <input
                      id="verificationCode"
                      type="text"
                      required
                      maxLength={6}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                      placeholder="Enter 6-digit code"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading || verificationCode.length !== 6}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? "Verifying..." : "Verify Code"}
                </button>
              </form>
            </div>
          )}

          {(message || error) && (
            <div
              className={`mt-4 p-4 rounded-md ${
                error ? "bg-red-50 text-red-700" : "bg-green-50 text-green-700"
              }`}
            >
              <p className="text-sm font-medium">{error || message}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Enable2FA;
